import { Stack } from '@mui/material';
import { useAuth } from '@schooly/components/authentication';
import { PageHeader } from '@schooly/components/filters';
import { DROPDOWN } from '@schooly/constants/test-ids';
import { AccessDenied, SelectPopover } from '@schooly/style';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useSearchParams } from 'react-router-dom';

import { PermissionGuard } from '../../components/Guards';
import MainLayout from '../../components/uikit-components/MainLayout/MainLayout';
import { allowRenderDashboard } from '../../helpers/renderRoutesByPermissions';
import {
  TEACHER_DASHBOARD_PERMISSIONS,
  useTeacherDashboardPermissions,
} from './useTeacherDashboardPermissions';
import { DashboardContent } from './DashboardContent';
import { TeacherDashboardContent } from './TeacherDashboardContent';
import { ThreadsWidget } from './Widgets/Threads/ThreadsWidget';

type DashboardOption = { id: 'teacher' | 'admissions'; name: string };

const VIEW_PARAM = 'view';

const POPOVER_SX = {
  '& .MuiList-root': {
    width: 'auto',
    minWidth: 220,
    p: 1,
  },
};

export const DashboardPage: FC = () => {
  const { permissions } = useAuth();
  const canSeeDashboard = allowRenderDashboard(permissions);
  const { canViewTeacherDashboard, canViewMessages } = useTeacherDashboardPermissions();
  const { formatMessage } = useIntl();

  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get(VIEW_PARAM);

  const dashboardOptions = useMemo<DashboardOption[]>(
    () => [
      { id: 'teacher', name: formatMessage({ id: 'dashboard-TeacherDashboard' }) },
      { id: 'admissions', name: formatMessage({ id: 'dashboard-AdmissionsDashboard' }) },
    ],
    [formatMessage],
  );

  const defaultView: DashboardOption['id'] = canViewTeacherDashboard ? 'teacher' : 'admissions';

  const resolveView = (): DashboardOption['id'] => {
    if (viewParam === 'teacher' && canViewTeacherDashboard) return viewParam;
    if (viewParam === 'admissions') return viewParam;
    return defaultView;
  };

  const selectedView = resolveView();

  const needsViewParam = viewParam === null || viewParam !== selectedView;

  useEffect(() => {
    if (!pathname.startsWith('/dashboard')) return;
    if (!needsViewParam) return;

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(VIEW_PARAM, selectedView);
        return next;
      },
      { replace: true },
    );
  }, [pathname, needsViewParam, selectedView, setSearchParams]);

  const selectedOption = dashboardOptions.find((o) => o.id === selectedView);

  const handleSelect = useCallback(
    (option: DashboardOption) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set(VIEW_PARAM, option.id);
        return next;
      });
    },
    [setSearchParams],
  );

  if (!canSeeDashboard) {
    return (
      <MainLayout>
        <AccessDenied />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Stack>
        <PageHeader
          pageTitle={
            canViewTeacherDashboard ? (
              <SelectPopover
                options={dashboardOptions}
                selectedOption={selectedOption}
                onSelect={handleSelect}
                popoverProps={{ sx: POPOVER_SX }}
                dataTestId={DROPDOWN.DASHBOARD}
              />
            ) : (
              formatMessage({ id: 'dashboard-AdmissionsDashboard' })
            )
          }
          actionsContent={
            selectedView === 'teacher' && canViewMessages ? <ThreadsWidget /> : undefined
          }
        />

        {selectedView === 'teacher' ? (
          <PermissionGuard permissions={TEACHER_DASHBOARD_PERMISSIONS} fallback={<AccessDenied />}>
            <TeacherDashboardContent />
          </PermissionGuard>
        ) : (
          <DashboardContent />
        )}
      </Stack>
    </MainLayout>
  );
};
